/**
 * SEO Utilities
 *
 * Provides functions for managing SEO metadata, schema markup,
 * and dynamic meta tags for Optioneer.io
 */

import seoConfig from '../config/seo-config.json'
import { sendGoogleAnalyticsEvent } from './consent/googleTag'

export interface PageSEO {
  title: string
  description: string
  keywords?: string[]
  canonical: string
  og?: {
    title: string
    description: string
    type: string
    image?: string
  }
  twitter?: {
    card: string
    title: string
    description: string
    image?: string
  }
  schema?: Array<Record<string, unknown>>
  noindex?: boolean
}

function getPriceValidUntilOneYearOut(baseDate: Date = new Date()): string {
  const nextYear = new Date(baseDate)
  nextYear.setUTCFullYear(nextYear.getUTCFullYear() + 1)
  return nextYear.toISOString().slice(0, 10)
}

function enrichSchemaValue(value: unknown, priceValidUntil: string): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => enrichSchemaValue(item, priceValidUntil))
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const schemaObject = value as Record<string, unknown>
  const enrichedObject: Record<string, unknown> = {}

  Object.entries(schemaObject).forEach(([key, nestedValue]) => {
    enrichedObject[key] = enrichSchemaValue(nestedValue, priceValidUntil)
  })

  if (enrichedObject['@type'] === 'Offer' && enrichedObject.price !== undefined) {
    enrichedObject.priceValidUntil = priceValidUntil
  }

  return enrichedObject
}

function enrichSchemaMarkup(
  schema: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
  const priceValidUntil = getPriceValidUntilOneYearOut()
  return schema.map(
    (schemaItem) => enrichSchemaValue(schemaItem, priceValidUntil) as Record<string, unknown>
  )
}

/**
 * Get SEO configuration for a specific page
 */
export function getPageSEO(pathname: string): PageSEO | null {
  const pageConfig = (seoConfig.pages as Record<string, PageSEO | undefined>)[pathname]

  if (!pageConfig) {
    return null
  }

  return {
    title: pageConfig.title,
    description: pageConfig.description,
    keywords: pageConfig.keywords,
    canonical: pageConfig.canonical,
    og: pageConfig.og || {
      title: pageConfig.title,
      description: pageConfig.description,
      type: seoConfig.defaultMeta.type,
    },
    twitter: pageConfig.twitter || {
      card: 'summary_large_image',
      title: pageConfig.title,
      description: pageConfig.description,
    },
    schema: pageConfig.schema,
    noindex: pageConfig.noindex || false,
  }
}

/**
 * Update document meta tags
 */
export function updateMetaTags(seo: PageSEO) {
  // Update title
  document.title = seo.title

  // Update or create meta tags
  const metaTags: Record<string, string> = {
    description: seo.description,
    ...(seo.keywords?.length ? { keywords: seo.keywords.join(', ') } : {}),
    'og:title': seo.og?.title || seo.title,
    'og:description': seo.og?.description || seo.description,
    'og:type': seo.og?.type || seoConfig.defaultMeta.type,
    'og:url': seo.canonical,
    'og:image': seo.og?.image || seoConfig.defaultMeta.defaultImage,
    'og:site_name': seoConfig.defaultMeta.siteName,
    'og:locale': seoConfig.defaultMeta.locale,
    'twitter:card': seo.twitter?.card || 'summary_large_image',
    'twitter:url': seo.canonical,
    'twitter:title': seo.twitter?.title || seo.title,
    'twitter:description': seo.twitter?.description || seo.description,
    'twitter:image': seo.twitter?.image || seoConfig.defaultMeta.defaultImage,
    'twitter:site': seoConfig.defaultMeta.twitterHandle,
    'twitter:creator': seoConfig.defaultMeta.twitterHandle,
    robots: seo.noindex ? 'noindex, nofollow' : 'index, follow',
  }

  Object.entries(metaTags).forEach(([name, content]) => {
    if (!content) return

    const isOg = name.startsWith('og:')
    const isTwitter = name.startsWith('twitter:')
    const selector = isOg ? 'property' : 'name'
    let meta = document.querySelector(`meta[${selector}="${name}"]`)

    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(selector, name)
      document.head.appendChild(meta)
    }

    meta.setAttribute('content', content)

    if (isTwitter) {
      const legacyTwitterMeta = document.querySelector(`meta[property="${name}"]`)
      if (legacyTwitterMeta) {
        legacyTwitterMeta.setAttribute('content', content)
      }
    }
  })

  // Update canonical link
  let canonical = document.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', seo.canonical)
}

/**
 * Inject schema markup as JSON-LD
 */
export function injectSchemaMarkup(schema: Array<Record<string, unknown>> | undefined) {
  // Only remove dynamically-added schema scripts (preserves base Organization schema from index.html)
  const existing = document.querySelectorAll(
    'script[type="application/ld+json"][data-dynamic="true"]'
  )
  existing.forEach((el) => el.remove())

  if (!schema || schema.length === 0) return

  const enrichedSchema = enrichSchemaMarkup(schema)

  enrichedSchema.forEach((schemaItem) => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-dynamic', 'true')
    script.text = JSON.stringify(schemaItem)
    document.head.appendChild(script)
  })
}

/**
 * Get conversational AI-optimized content
 * This helps with ChatGPT, Perplexity, and Gemini search results
 */
export function getAISearchContent(pathname: string): string {
  const aiContent: Record<string, string> = {
    '/': `Optioneer is an options portfolio tracker designed specifically for retail traders.
          It connects options portfolio tracking, P&L and Greeks analysis, Discovery and Saved Screens,
          hypothetical Scenario Lab testing, and position management with Roll Assistant. It does not execute trades.`,
    '/signup': `You can create an Optioneer account in just 60 seconds.
               Sign up with your email or Google account to try options tracking with P&L and Greeks calculations.`,
    '/faq': `Common questions about Optioneer cover portfolio tracking, multi-leg strategies, the Wheel,
            read-only Broker Sync, Scenario Lab, Roll Assistant, Your Edge, Discovery, Saved Screens,
            iOS and Android apps, and the 7-day Premium trial.`,
    '/features/scenario-lab': `Scenario Lab tests a hypothetical stock or options trade against the actual
            portfolio selected by the user. It can estimate payoff, cash, concentration, and Greek changes,
            and it never changes the actual portfolio or submits an order.`,
    '/features/roll-assistant': `Roll Assistant compares eligible replacement contracts using current
            option-chain data. It uses deterministic filters and sorting, does not recommend a candidate,
            and does not execute the roll.`,
  }

  return aiContent[pathname] || ''
}

/**
 * Generate structured data for breadcrumbs
 */
export function generateBreadcrumbSchema(pathname: string): Record<string, unknown> | null {
  const paths = pathname.split('/').filter(Boolean)
  if (paths.length === 0) return null

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: seoConfig.defaultMeta.siteUrl,
    },
  ]

  let currentPath = ''
  paths.forEach((path, index) => {
    currentPath += `/${path}`
    itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' '),
      item: `${seoConfig.defaultMeta.siteUrl}${currentPath}`,
    })
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  }
}

/**
 * Track search query for analytics
 * Helps identify what users are looking for
 */
export function trackSearchQuery(query: string, source: string = 'direct') {
  // This can be expanded to send to analytics
  // console.log('[SEO] Search query tracked:', { query, source })

  sendGoogleAnalyticsEvent('search', {
    search_term: query,
    search_source: source,
  })
}
